import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Wrench,
  Plus,
  CheckCircle,
  Clock,
  AlertTriangle,
  Trash2,
  Play,
  Edit2,
  Save,
  X,
  Euro
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function RepairTasksManager({ scooterId, receptionData, onUpdate, mechanics = [] }) {
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [newTask, setNewTask] = useState({
    task_name: "",
    description: "",
    estimated_cost: 0,
    assigned_mechanic: "",
    priority: 2
  });

  const tasks = receptionData?.repair_tasks || [];
  const completedCount = tasks.filter(t => t.status === "completed").length;
  const progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const handleAddTask = async () => {
    if (!newTask.task_name.trim()) {
      toast.error("Le nom de la tache est requis");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/scooters/${scooterId}/repair-tasks`, newTask);
      toast.success("Tache ajoutee");
      setShowAddTask(false);
      setNewTask({
        task_name: "",
        description: "",
        estimated_cost: 0,
        assigned_mechanic: "",
        priority: 2
      });
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus, actualCost = null) => {
    setLoading(true);
    try {
      const update = { status: newStatus };
      if (actualCost !== null) {
        update.actual_cost = actualCost;
      }
      await api.put(`/scooters/${scooterId}/repair-tasks/${taskId}`, update);
      toast.success("Tache mise a jour");
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    setLoading(true);
    try {
      await api.put(`/scooters/${scooterId}/repair-tasks/${taskId}`, updates);
      toast.success("Tache mise a jour");
      setEditingTask(null);
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("Supprimer cette tache ?")) return;
    
    setLoading(true);
    try {
      await api.delete(`/scooters/${scooterId}/repair-tasks/${taskId}`);
      toast.success("Tache supprimee");
      onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 1:
        return <Badge className="bg-red-500 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Urgent</Badge>;
      case 2:
        return <Badge className="bg-yellow-500 text-black">Normal</Badge>;
      case 3:
        return <Badge className="bg-blue-500 text-white">Peut attendre</Badge>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />A faire</Badge>;
      case "in_progress":
        return <Badge className="bg-orange-500 text-white"><Play className="w-3 h-3 mr-1" />En cours</Badge>;
      case "completed":
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Termine</Badge>;
      default:
        return null;
    }
  };

  // Calculate totals
  const totalEstimated = tasks.reduce((sum, t) => sum + (t.estimated_cost || 0), 0);
  const totalActual = tasks.reduce((sum, t) => sum + (t.actual_cost || 0), 0);

  return (
    <Card className="border-orange-500/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wrench className="w-5 h-5 text-orange-500" />
            Taches de Reparation
          </CardTitle>
          <Button size="sm" onClick={() => setShowAddTask(true)} data-testid="btn-add-repair-task">
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Progression</span>
            <span className="font-mono">{completedCount}/{tasks.length} taches</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Cost Summary */}
        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-secondary/50 rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground">Cout estime total</p>
            <p className="font-bold text-lg">{totalEstimated} EUR</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Cout reel total</p>
            <p className="font-bold text-lg text-primary">{totalActual} EUR</p>
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-3">
          {tasks.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              Aucune tache de reparation definie
            </p>
          ) : (
            tasks.map((task) => (
              <div
                key={task.task_id}
                className={`p-3 rounded-lg border ${
                  task.status === "completed"
                    ? "bg-green-500/10 border-green-500/30"
                    : task.status === "in_progress"
                    ? "bg-orange-500/10 border-orange-500/30"
                    : "bg-secondary/50 border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getPriorityBadge(task.priority)}
                      {getStatusBadge(task.status)}
                      {task.assigned_mechanic && (
                        <Badge variant="outline" className="text-xs">
                          {task.assigned_mechanic}
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-medium mt-2">{task.task_name}</h4>
                    {task.description && (
                      <p className="text-sm text-muted-foreground">{task.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Est: {task.estimated_cost} EUR</span>
                      {task.actual_cost > 0 && (
                        <span className="text-primary font-medium">Reel: {task.actual_cost} EUR</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {task.status === "pending" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleUpdateTaskStatus(task.task_id, "in_progress")}
                        title="Demarrer"
                      >
                        <Play className="w-4 h-4 text-orange-500" />
                      </Button>
                    )}
                    {task.status === "in_progress" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingTask(task)}
                        title="Terminer"
                      >
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingTask(task)}
                      title="Modifier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteTask(task.task_id)}
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une Tache de Reparation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="task_name">Nom de la tache *</Label>
              <Input
                id="task_name"
                placeholder="Ex: Changer les plaquettes de frein"
                value={newTask.task_name}
                onChange={(e) => setNewTask({ ...newTask, task_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Details supplementaires..."
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="estimated_cost">Cout estime (EUR)</Label>
                <Input
                  id="estimated_cost"
                  type="number"
                  value={newTask.estimated_cost}
                  onChange={(e) => setNewTask({ ...newTask, estimated_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="priority">Priorite</Label>
                <Select
                  value={String(newTask.priority)}
                  onValueChange={(v) => setNewTask({ ...newTask, priority: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Urgent</SelectItem>
                    <SelectItem value="2">Normal</SelectItem>
                    <SelectItem value="3">Peut attendre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="mechanic">Mecanicien assigne</Label>
              <Input
                id="mechanic"
                placeholder="Nom du mecanicien"
                value={newTask.assigned_mechanic}
                onChange={(e) => setNewTask({ ...newTask, assigned_mechanic: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTask(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddTask} disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={!!editingTask} onOpenChange={() => setEditingTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la Tache</DialogTitle>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-4">
              <div>
                <Label>Nom de la tache</Label>
                <p className="font-medium">{editingTask.task_name}</p>
              </div>
              <div>
                <Label htmlFor="actual_cost">Cout reel (EUR)</Label>
                <Input
                  id="actual_cost"
                  type="number"
                  defaultValue={editingTask.actual_cost || editingTask.estimated_cost}
                  onChange={(e) => {
                    editingTask.actual_cost = parseFloat(e.target.value) || 0;
                  }}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  defaultValue={editingTask.notes}
                  placeholder="Notes sur la reparation..."
                  onChange={(e) => {
                    editingTask.notes = e.target.value;
                  }}
                />
              </div>
              <div>
                <Label htmlFor="status">Statut</Label>
                <Select
                  defaultValue={editingTask.status}
                  onValueChange={(v) => {
                    editingTask.status = v;
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">A faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="completed">Termine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTask(null)}>
              Annuler
            </Button>
            <Button
              onClick={() => handleUpdateTask(editingTask.task_id, {
                status: editingTask.status,
                actual_cost: editingTask.actual_cost,
                notes: editingTask.notes
              })}
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
